import { useState } from "react";
import { Stack, Button, CircularProgress, Tooltip } from "@material-ui/core";
import { Icon } from "@iconify/react";
import refreshFill from "@iconify/icons-eva/refresh-fill";
import fileTextFill from "@iconify/icons-eva/file-text-fill";
import downloadFill from "@iconify/icons-eva/download-fill";
import undoFill from "@iconify/icons-eva/undo-fill";
import { useSnackbar } from "src/components/snackbar";
import { AlertaModalV2 } from "src/components/AlertaSwall";
import {
  usePrepararPeriodoMutation,
  useFacturarPeriodoMutation,
  useReintentarFacturacionMutation,
  backendToUrlFecha,
} from "src/services/facturasApi";
import { exportarFacturacionExcel } from "src/components/facturacion/exportFacturacionExcel";
import { contarDetenidos } from "src/components/facturacion/estadoFacturacion";
import { mensajeDeError, statusDeError } from "src/utils/manejo-errores";
import ResumenCambiosDialog from "src/components/facturacion/ResumenCambiosDialog";

export default function AccionesFacturacion({
  sucursal,
  data,
  items,
  itemsFiltrados,
  permiso,
  soloExportar,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [preparar, { isLoading: preparando }] = usePrepararPeriodoMutation();
  const [facturar, { isLoading: facturando }] = useFacturarPeriodoMutation();
  const [reintentarTodos, { isLoading: reintentandoTodos }] =
    useReintentarFacturacionMutation();
  const [cambios, setCambios] = useState(null);
  const [verCambios, setVerCambios] = useState(false);

  const totalErrores = data?.totales?.errores ?? 0;
  const hayPendientes = (data?.totales?.pendientes ?? 0) > 0;
  const hayErrores = totalErrores > 0;
  // Los detenidos por reconciliacion vienen dentro de errores, pero el barrido masivo los
  // salta: anunciar el total de errores prometeria reintentar ítems que no se van a tocar.
  const detenidos = data?.totales?.revision ?? contarDetenidos(items);
  const reintentables = Math.max(totalErrores - detenidos, 0);
  // Emisión activa = drain realmente en curso. Exige lease.activo (owner de ESTE
  // periodo) ademas de heartbeat fresco: tras liberar, el heartbeat queda reciente
  // por STALE_SECONDS aunque ya no haya drain, asi que heartbeatFresco solo daria
  // falso positivo y bloquearia "Facturar" sin razon. Mismo criterio que el polling.
  const emisionActiva =
    data?.estado === "FACTURANDO" ||
    (data?.lease?.activo && data?.lease?.heartbeatFresco);

  const onActualizar = async () => {
    try {
      const res = await preparar(sucursal).unwrap();
      setCambios(res.cambios ?? null);
      setVerCambios(true);
    } catch (e) {
      // Un 409 puede venir por más de un motivo; el mensaje del backend nombra cuál, y el
      // texto propio queda de respaldo por si la respuesta no trae ninguno.
      const msg =
        statusDeError(e) === 409
          ? mensajeDeError(
              e,
              "Hay una facturación en curso; espere a que termine para actualizar los planes.",
            )
          : "No se pudieron actualizar los planes. Intente nuevamente.";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const onFacturar = () => {
    AlertaModalV2({
      title: "¿Facturar todos los pendientes?",
      text: "Esta acción emite los documentos de la sucursal. Es irreversible.",
      confirmButtonText: "Sí, facturar",
      cancelButtonText: "Cancelar",
      icon: "warning",
    })
      .fire()
      .then(async (r) => {
        if (!r.isConfirmed) return;
        try {
          await facturar(sucursal).unwrap();
          enqueueSnackbar(
            "Facturación iniciada. El avance se actualiza en vivo.",
            {
              variant: "success",
            },
          );
        } catch (e) {
          const msg =
            statusDeError(e) === 409
              ? mensajeDeError(e, "Ya hay una facturación en curso.")
              : "No se pudo iniciar la facturación. Intente nuevamente.";
          enqueueSnackbar(msg, { variant: "error" });
        }
      });
  };

  const onReintentarTodos = () => {
    const avisoDetenidos =
      detenidos > 0
        ? ` Los ${detenidos} detenido${detenidos === 1 ? "" : "s"} por reconciliación quedan fuera: hay que resolverlos antes de poder reintentarlos.`
        : "";
    AlertaModalV2({
      title: "¿Reintentar todos los errores?",
      text: `Se reintentarán ${reintentables} ítem${reintentables === 1 ? "" : "s"} en error. Se facturarán en la próxima facturación.${avisoDetenidos}`,
      confirmButtonText: "Sí, reintentar",
      cancelButtonText: "Cancelar",
      icon: "warning",
    })
      .fire()
      .then(async (r) => {
        if (!r.isConfirmed) return;
        try {
          await reintentarTodos({ sucursal }).unwrap();
          enqueueSnackbar("Errores marcados para reintentar.", {
            variant: "success",
          });
        } catch (e) {
          const msg =
            statusDeError(e) === 409
              ? mensajeDeError(
                  e,
                  "Hay una facturación en curso; espere para reintentar.",
                )
              : "No se pudieron reintentar los errores.";
          enqueueSnackbar(msg, { variant: "error" });
        }
      });
  };

  const onExportar = async () => {
    if (!itemsFiltrados.length) {
      enqueueSnackbar("No hay filas para exportar con los filtros actuales.", {
        variant: "info",
      });
      return;
    }
    const nombre = `facturacion-${sucursal}-${backendToUrlFecha(data?.fecha) ?? "periodo"}`;
    try {
      await exportarFacturacionExcel(itemsFiltrados, nombre);
    } catch (e) {
      enqueueSnackbar("No se pudo generar el archivo Excel.", {
        variant: "error",
      });
    }
  };

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="flex-end"
      >
        {!soloExportar && (
          <>
            <Button
              variant="outlined"
              startIcon={
                preparando ? (
                  <CircularProgress size={18} />
                ) : (
                  <Icon icon={refreshFill} />
                )
              }
              disabled={!permiso.puedeFacturar || preparando || emisionActiva}
              onClick={onActualizar}
            >
              Actualizar planes
            </Button>
            <Button
              variant="contained"
              startIcon={
                facturando ? (
                  <CircularProgress size={18} />
                ) : (
                  <Icon icon={fileTextFill} />
                )
              }
              disabled={
                !permiso.puedeFacturar ||
                facturando ||
                emisionActiva ||
                !hayPendientes
              }
              onClick={onFacturar}
            >
              Facturar todo
            </Button>
            {hayErrores && (
              // Con todos los errores detenidos el botón se muestra deshabilitado en vez de
              // desaparecer: la explicación de por qué no se puede reintentar es justamente
              // lo que hay que decir. El span sostiene el tooltip, que un botón deshabilitado
              // no emite eventos de puntero.
              <Tooltip
                title={
                  reintentables === 0
                    ? "Todos los errores están detenidos por reconciliación; hay que resolverlos antes de reintentarlos."
                    : ""
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={
                      reintentandoTodos ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Icon icon={undoFill} />
                      )
                    }
                    disabled={
                      reintentandoTodos || emisionActiva || reintentables === 0
                    }
                    onClick={onReintentarTodos}
                  >
                    Reintentar errores
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        )}
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<Icon icon={downloadFill} />}
          onClick={onExportar}
        >
          Exportar a Excel
        </Button>
      </Stack>
      <ResumenCambiosDialog
        cambios={cambios}
        open={verCambios}
        onClose={() => setVerCambios(false)}
      />
    </>
  );
}
